import { useState } from 'react'
import CheckCircleIcon from '../../icons/CheckCircleIcon'
import LockIcon from '../../icons/LockIcon'
import { Text } from '../Text'
import { Toggle } from '../Toggle'
import type { ComponentProps, HrReviewFlagCustomProps } from './types'

interface ExistingFlag {
	id: string
	status: 'pending' | 'in_review' | 'resolved'
}

type FormsHRReviewFlagProps = ComponentProps & {
	readOnly?: boolean
	existingFlag?: ExistingFlag | null
	onSubmitFlag?: (input: { reasonText: string }) => Promise<void>
}

const DEFAULTS: HrReviewFlagCustomProps = {
	widgetLabel: 'HR Review Flag',
	toggleLabel: 'Request HR Review',
	helperText: 'Flag this submission for a confidential conversation with HR. Your manager will not be notified.',
	reasonRequired: true,
	reasonPrompt: 'What would you like to discuss?',
	reasonPlaceholder:
		'Briefly describe the topic you’d like to discuss with HR. This is confidential and will only be visible to the HR team…',
	showConfidentialityFooter: true,
	notifyHrOnFlag: true,
	confirmationMessage: 'A member of the HR team will reach out to schedule a conversation.',
}

export function FormsHRReviewFlag({ component, readOnly, existingFlag, onSubmitFlag }: FormsHRReviewFlagProps) {
	const custom = (component.custom ?? {}) as Partial<HrReviewFlagCustomProps>
	const config: HrReviewFlagCustomProps = { ...DEFAULTS, ...custom }

	const alreadySubmitted = Boolean(existingFlag)
	const [toggled, setToggled] = useState(false)
	const [reason, setReason] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [submitted, setSubmitted] = useState(alreadySubmitted)
	const [error, setError] = useState<string | null>(null)

	if (submitted || alreadySubmitted) {
		return (
			<div
				className='flex items-start gap-3 rounded-md border border-success-200 bg-success-50 px-4 py-3'
				data-testid='hr-review-flag-confirmation'>
				<CheckCircleIcon size={20} className='fill-success-600 mt-0.5 shrink-0' />
				<div className='flex flex-col gap-1'>
					<Text variant='small' weight='semibold' className='text-success-800'>
						HR review requested
					</Text>
					<Text variant='small' className='text-success-700'>
						{config.confirmationMessage}
					</Text>
				</div>
			</div>
		)
	}

	const reasonInvalid = config.reasonRequired && reason.trim().length === 0
	const submitDisabled = readOnly || submitting || reasonInvalid || !onSubmitFlag

	const handleSubmit = async () => {
		if (!onSubmitFlag) return
		setSubmitting(true)
		setError(null)
		try {
			await onSubmitFlag({ reasonText: reason.trim() })
			setSubmitted(true)
		} catch (err) {
			console.error('HR review flag submit failed', err)
			setError(err instanceof Error ? err.message : 'Could not submit flag. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<section
			data-testid='hr-review-flag-widget'
			// Typography, sizes, and colors are all pinned to the design mock —
			// inline styles win where Tailwind's predefined Text variants would
			// land on neighboring sizes (13/11/10/9 px). UI-only.
			style={{ borderColor: '#e4e7ec' }}
			className='w-full rounded-md border bg-white p-4'>
			<div className='flex items-start gap-3'>
				<Toggle
					checked={toggled}
					onCheckedChange={(value) => {
						setToggled(value)
						if (!value) {
							setReason('')
							setError(null)
						}
					}}
					disabled={readOnly}
				/>
				<div className='flex flex-col gap-0.5'>
					<span style={{ fontSize: 13, fontWeight: 600, color: '#0f1729', lineHeight: 1.3 }}>{config.toggleLabel}</span>
					<span style={{ fontSize: 10, color: '#637083', lineHeight: 1.5 }}>{config.helperText}</span>
				</div>
			</div>

			{toggled && (
				<div className='mt-3.5 flex flex-col gap-2 border-t pt-3.5' style={{ borderTopColor: '#f2f4f7' }}>
					<div className='flex flex-col gap-1.5'>
						<span style={{ fontSize: 11, fontWeight: 600, color: '#0f1729' }}>
							{config.reasonPrompt}
							{config.reasonRequired && (
								<span className='ml-1' style={{ fontSize: 10, fontWeight: 400, color: '#9aa4b2' }}>
									(required)
								</span>
							)}
						</span>
						<textarea
							value={reason}
							onChange={(event) => setReason(event.target.value)}
							placeholder={config.reasonPlaceholder}
							rows={4}
							disabled={readOnly || submitting}
							maxLength={5000}
							data-testid='hr-review-flag-reason'
							style={{
								width: '100%',
								minHeight: 80,
								padding: '10px 12px',
								border: '1px solid #e4e7ec',
								borderRadius: 8,
								fontSize: 11,
								color: '#364152',
								lineHeight: 1.6,
								resize: 'vertical',
								background: '#f8f9fb',
								boxSizing: 'border-box',
								outline: 'none',
							}}
						/>
					</div>
					{error && <span style={{ fontSize: 10, color: '#dc2626' }}>{error}</span>}
					<div className='flex items-center justify-between gap-3 mt-1'>
						{config.showConfidentialityFooter ? (
							<div className='flex items-center gap-1' style={{ color: '#9aa4b2' }}>
								<LockIcon size={12} className='fill-current' />
								<span style={{ fontSize: 9, color: '#9aa4b2' }}>Confidential — visible to HR only</span>
							</div>
						) : (
							<span />
						)}
						<button
							type='button'
							onClick={handleSubmit}
							disabled={submitDisabled}
							data-testid='hr-review-flag-submit'
							style={{
								padding: '6px 16px',
								borderRadius: 6,
								border: 'none',
								fontSize: 11,
								fontWeight: 600,
								cursor: submitDisabled ? 'default' : 'pointer',
								background: submitDisabled ? '#e4e7ec' : '#4a6cf7',
								color: submitDisabled ? '#9aa4b2' : '#ffffff',
								transition: 'background 0.15s',
							}}>
							{submitting ? 'Submitting…' : 'Submit Flag'}
						</button>
					</div>
				</div>
			)}
		</section>
	)
}
